import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Chip, IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ConfirmDialog } from '@components/dialogs/ConfirmDialog';
import { type ColumnOptions, DataTable } from '@components/tables/DataTable';
import { useSearchEmployeesQuery, useDeleteEmployeeMutation } from '@features/redux/apis/employees';
import { useSelector } from 'react-redux';
import type { RootState } from '@features/redux/store';
import type { EmployeeSearchParams, EmployeeSearchResult } from '@/types/newEmployees';

interface EmployeesTableProps {
    orgFilters: {
        companyIds: string;
        resellerIds: string;
        locationIds: string;
    };
    /** Optional search text passed from FiltersDrawer */
    searchText?: string;
    onDelete?: (e: EmployeeSearchResult) => void;
    onSelect?: (e: EmployeeSearchResult) => void;
}

const PAGE_SIZE = 25;

// Extend the API result to include a synthetic "actions" field so we can
// render row-level controls while satisfying strict type checks.
interface EmployeeRow extends EmployeeSearchResult {
    actions: null;
}

export const EmployeesTable = ({ orgFilters, searchText, onDelete, onSelect }: EmployeesTableProps) => {
    // DataTable is 1-indexed, so default to page 1
    const [page, setPage] = useState<number>(1);

    // Access auth token for secured API requests
    const IdToken = useSelector((state: RootState) => state.auth.IdToken);

    // Delete employee mutation with RTK Query
    const [deleteEmployee, { isLoading: isDeleting }] = useDeleteEmployeeMutation();

    // Track a row that the user has requested to delete; when non-null, we will show a confirmation dialog.
    const [pendingDelete, setPendingDelete] = useState<EmployeeSearchResult | null>(null);

    const searchParams: EmployeeSearchParams = useMemo(() => {
        return {
            pageNum: page - 1, // API expects 0-based index
            pageSize: PAGE_SIZE,
            companyIds: orgFilters.companyIds || undefined,
            resellerIds: orgFilters.resellerIds || undefined,
            locationIds: orgFilters.locationIds || undefined,
            sortDirection: 'ASC',
            searchText: searchText?.trim() || undefined,
        };
    }, [orgFilters, page, searchText]);

    const { data, isLoading } = useSearchEmployeesQuery(searchParams);

    const employees: EmployeeRow[] = useMemo(
        () => (data?.employees || []).map((e) => ({ ...e, actions: null })),
        [data]
    );

    const columns: ColumnOptions<any>[] = useMemo(
        () => [
            { field: 'Name', headerName: 'Name', flex: 1, minWidth: 160 },
            { field: 'Email', headerName: 'Email', flex: 1, minWidth: 200 },
            {
                field: 'ParentCompanyName',
                headerName: 'Company',
                flex: 1,
                minWidth: 160,
                renderCell: (params: any) => (
                    <Link
                        to={`/manage/organizations?type=company&id=${params.row.ParentCompanyID}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ textDecoration: 'underline' }}>
                        {params.value}
                    </Link>
                ),
            },
            {
                field: 'ResellerName',
                headerName: 'Reseller',
                flex: 1,
                minWidth: 160,
                renderCell: (params: any) =>
                    params.row.ResellerName ? (
                        <Link
                            to={`/manage/organizations?type=reseller&id=${params.row.ResellerID}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{ textDecoration: 'underline' }}>
                            {params.row.ResellerName}
                        </Link>
                    ) : (
                        '—'
                    ),
            },
            {
                field: 'LocationName',
                headerName: 'Location',
                flex: 1,
                minWidth: 160,
                renderCell: (params: any) => (
                    <Link
                        to={`/manage/organizations?type=location&id=${params.row.LocationID}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ textDecoration: 'underline' }}>
                        {params.value}
                    </Link>
                ),
            },

            {
                field: 'Status',
                headerName: 'Status',
                width: 120,
                renderCell: ({ row }: { row: EmployeeSearchResult }) => (
                    <Chip label={row.Status} color={row.Status === 'Active' ? 'success' : 'default'} size='small' />
                ),
            },
            {
                field: 'actions',
                headerName: '',
                width: 100,
                sortable: false,
                // We don't want the automatic value rendering – just the action button.
                ignoreValue: true,
                renderExtraCellContent: (row) => {
                    const RowActions = ({ targetRow }: { targetRow: EmployeeSearchResult }) => {
                        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

                        const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
                            event.stopPropagation();
                            setAnchorEl(event.currentTarget);
                        };

                        const handleClose = () => setAnchorEl(null);

                        const handleDelete = () => {
                            setPendingDelete(targetRow);
                            handleClose();
                        };

                        return (
                            <>
                                <IconButton size='small' onClick={handleOpen}>
                                    <MoreVertIcon fontSize='small' />
                                </IconButton>

                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleClose}
                                    // Prevent menu clicks from triggering row selection
                                    onClick={(e) => e.stopPropagation()}
                                    PaperProps={{ sx: { minWidth: 160 } }}>
                                    <MenuItem onClick={handleDelete} dense sx={{ color: 'text.secondary' }}>
                                        <DeleteOutlineIcon sx={{ color: 'text.disabled', mr: 1 }} fontSize='small' />
                                        Delete
                                    </MenuItem>
                                </Menu>
                            </>
                        );
                    };

                    return <RowActions targetRow={row} />;
                },
            },
        ],
        []
    );

    return (
        <>
            <DataTable
                pinnedColumns={{ right: ['actions'] }}
                storageKey='Employees'
                rows={employees}
                columns={columns}
                totalRowCount={data?.totalRecords || 0}
                displayTotal={false}
                pageSize={PAGE_SIZE}
                page={page}
                getUniqueRowId={(row) => `employee-${row.EmployeeID}`}
                loading={isLoading}
                onPaginate={setPage}
                onRowClick={({ row }: any) => onSelect?.(row)}
                // Prevent built-in menus to match SystemTable behaviour
                menus={false}
                height={'calc(100dvh - 250px)'}
            />

            {pendingDelete && (
                <ConfirmDialog
                    confirmText={`Are you sure you want to delete employee ${pendingDelete.Name}?`}
                    onCancel={() => {
                        if (!isDeleting) setPendingDelete(null);
                    }}
                    onConfirm={async () => {
                        if (!pendingDelete) return;

                        try {
                            await deleteEmployee({ id: pendingDelete.EmployeeID!, token: IdToken! }).unwrap();
                            // Optionally notify parent or toast here
                        } finally {
                            setPendingDelete(null);
                        }
                    }}
                    isLoading={isDeleting}
                />
            )}
        </>
    );
};
